import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios'
import { ElMessage, ElNotification } from 'element-plus'
import { ResultData, ConfigType } from './interface'
import { showLoading, hideLoading } from '@/components/baseFullScreenLoading'
import useUserStoreUserInfo from '@/store/modules/userInfo'
import router from '@/router'

/** 未配置时使用 30s，减少慢接口误杀；各环境可通过 VITE_HTTP_TIMEOUT_MS 覆盖（毫秒，须为正数） */
const resolveDefaultTimeoutMs = (): number => {
  const n = Number(import.meta.env.VITE_HTTP_TIMEOUT_MS)
  return Number.isFinite(n) && n > 0 ? n : 30000
}

/** noLoading 仍当静默。query / mutate / silent 交给页面，不进全屏计数。 */
const skipsGlobalLoading = (config?: ConfigType | null) => {
  if (!config) return false
  if (config.noLoading) return true
  const mode = config.loadingMode
  return mode === 'query' || mode === 'mutate' || mode === 'silent'
}

const config = {
  // baseURL: `${import.meta.env.VITE_APP_BASE_PATH}api`,
  baseURL: '/api',
  timeout: resolveDefaultTimeoutMs(),
  withCredentials: true
}

class RequestHttp {
  service: AxiosInstance
  public constructor(config: ConfigType) {
    this.service = axios.create(config) //实例化axios
    this.service.interceptors.request.use(
      (config: any) => {
        if (!skipsGlobalLoading(config)) {
          showLoading()
        }
        const userInfoStore = useUserStoreUserInfo()
        config.headers['ai-session-id'] = userInfoStore.aiSessionId
        const currentRoute = router.currentRoute.value
        if (
          currentRoute.name === 'DataQueryMining' ||
          currentRoute.name === 'DataQueryDrafting' ||
          currentRoute.name === 'DataQueryOaReply'
        ) {
          const raw = currentRoute.query.userId
          const value = Array.isArray(raw) ? raw[0] : raw
          const viewUserId = Number(value)
          if (Number.isFinite(viewUserId) && viewUserId > 0) {
            config.headers['X-Data-Query-User-Id'] = String(viewUserId)
          }
        }
        return {
          ...config
        }
      },
      (error: AxiosError) => {
        if (!skipsGlobalLoading(error.config as ConfigType | undefined)) {
          hideLoading()
        }
        return Promise.reject(error)
      }
    )

    //添加响应拦截器
    this.service.interceptors.response.use(
      (response: AxiosResponse) => {
        if (!skipsGlobalLoading(response.config as ConfigType)) {
          hideLoading()
        }
        /** 未登录 2000：先清 store。noAuthRedirect 为真时只 reject，不 Toast/跳登录（守卫静默探测）。 */
        if (response.data?.code === '2000') {
          const userInfoStore = useUserStoreUserInfo()
          userInfoStore.clearInfo()
          const suppressAuthRedirect = Boolean(
            (response.config as ConfigType).noAuthRedirect
          )
          if (!suppressAuthRedirect) {
            ElMessage.warning('登录已失效，请重新登录')
            if (router.currentRoute.value.name !== 'Login') {
              void router.replace({ name: 'Login' })
            }
          }
          return Promise.reject(response)
        }
        if (response.data instanceof Blob) {
          return response
        }
        if (response.data.code !== '200') {
          const msgText = String(response.data?.message ?? '')
          if (
            (msgText.includes('配额不足') || msgText.includes('尚未开通')) &&
            router.currentRoute.value.name !== 'Membership'
          ) {
            ElMessage.warning(msgText)
            void router.push({ name: 'Membership' })
            return Promise.reject(response)
          }
          const suppressErrorTip = Boolean(
            (response.config as ConfigType & { noError?: boolean }).noError
          )
          if (!suppressErrorTip) {
            ElNotification({
              title: '错误信息提示',
              dangerouslyUseHTMLString: true,
              message: response.data.message.replace(/\n/g, '<br>'),
              type: 'warning'
            })
          }
          return Promise.reject(response)
        }
        return response.data
      },
      (error: AxiosError) => {
        console.log(error, 'error')
        if (!skipsGlobalLoading(error.config as ConfigType | undefined)) {
          hideLoading()
        }
        const suppressErrorTip = Boolean(
          (error.config as ConfigType & { noError?: boolean })?.noError
        )
        if (!suppressErrorTip) {
          const status = error.response?.status
          if (status === 504) {
            ElMessage.error(
              '网关超时：服务端处理时间较长，请稍后重试；若持续出现，请联系管理员检查网关超时配置（如 Nginx proxy_read_timeout）。'
            )
          } else {
            ElMessage.error('请求异常，稍后重试！')
          }
        }
        return Promise.reject(error)
      }
    )
  }
  // * 常用请求方法封装
  get<T>(url: string, params?: object, _object = {}): Promise<ResultData<T>> {
    return this.service.get(url, {
      params,
      ..._object
    })
  }
  post<T>(url: string, params?: object, _object = {}): Promise<ResultData<T>> {
    return this.service.post(url, params, _object)
  }
  put<T>(url: string, params?: object, _object = {}): Promise<ResultData<T>> {
    return this.service.put(url, params, _object)
  }
  patch<T>(url: string, params?: object, _object = {}): Promise<ResultData<T>> {
    return this.service.patch(url, params, _object)
  }
  // * DELETE 请求方法封装
  delete<T>(
    url: string,
    params?: object,
    _object = {}
  ): Promise<ResultData<T>> {
    return this.service.delete(url, {
      params, // 查询参数（可选）
      ..._object // 其他配置项（如 headers 等）
    })
  }
}

export default new RequestHttp(config)
